import React, { useState, FC, useEffect } from 'react';

import {
  Box,
  Grid,
  Link,
  makeStyles,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';

import { useObserver } from 'mobx-react-lite';

import { isNullOrUndefined } from 'util';
import { pick, isEmpty, omit } from 'lodash';

import { OnlineOrder, Place } from 'kiolyn-api';

import CreditCardInput from '../../../Core/CreditCardInput/CreditCardInput';
import {
  useValidators,
  ValidationErrors,
  FieldValidators
} from '../../../Core/Validators';

import { LayoutVariant } from '../../../Core/CommonTypes';
import GooglePlaceAutocomplete from '../../../Core/GooglePlaceAutcocomplete/GooglePlaceAutocomplete';
import {
  getComponentAddressByPlace,
  PlaceType
} from '../../../../core/GoogleMaps';
import visa from 'payment-icons/min/flat/visa.svg';
import master from 'payment-icons/min/flat/mastercard.svg';
import amex from 'payment-icons/min/flat/amex.svg';
import discover from 'payment-icons/min/flat/discover.svg';
import creditCard from '../../../../static/images/credit-card-regular.svg';
import applePay from '../../../../static/images/cc-apple-pay.svg';
import PaymentMethod from './PaymentMethod';
import ApplePayStep from './ApplePayStep';
import { useOrdering } from '../../../../stores/OrderingStore';

const useStyles = makeStyles(theme => ({
  headline: {
    marginTop: theme.spacing(4)
  },
  inlineLink: {
    marginLeft: theme.spacing(2)
  },
  addressWrapper: {
    paddingRight: theme.spacing(2)
  },
  zipcode: {
    width: '20%'
  },
  address: {
    width: '80%',
    marginRight: '15px'
  },
  imgsvg: {
    height: '35px',
    width: '100%',
    margin: '3px'
  },
  blockCard: {
    marginLeft: '5px'
  },
  labelPayment: {
    whiteSpace: 'nowrap'
  }
}));

interface PaymentInfoProps {
  order: OnlineOrder;
  onErrorsChange?: (errors: ValidationErrors) => void;
  disabled?: boolean;
  variant?: LayoutVariant;
}

const PaymentInfo: FC<PaymentInfoProps> = ({
  order,
  onErrorsChange,
  variant
}) => {
  const classes = useStyles();
  const orderingStore = useOrdering();
  const isAcceptApplePay = orderingStore.store.accept_apple_pay;
  const [isManualAddress, setIsManualAddress] = useState(
    !!order.billing_street
  );
  const [ccErrors, setCCErrors] = useState<Record<string, any>>(
    typeof order.is_payment_by_apple_pay === 'boolean'
      ? {}
      : {
          isSelectPayment: false
        }
  );
  const filterErrors = (e: ValidationErrors) => {
    return omit(
      e,
      isManualAddress
        ? ['billing_place']
        : ['billing_street', 'billing_state', 'billing_city']
    );
  };
  const handleCCErrorsChange = (e: ValidationErrors) => {
    setCCErrors({ ...e });
    if (!isNullOrUndefined(onErrorsChange)) {
      onErrorsChange(filterErrors({ ...e, ...errors }));
    }
  };

  useEffect(() => {
    if (!order.billing_zip) {
      handleBillingPlaceChange(order.billing_place as PlaceType);
    }
    // eslint-disable-next-line
  }, [order]);

  const fieldValidators: FieldValidators = {
    billing_place: [
      (value?: Place) => {
        if (value?.description && value?.place_id) {
          return true;
        }
        return 'required';
      }
    ],
    billing_zip: [
      (value?: string) => {
        if (value && !isEmpty(value)) {
          return true;
        }
        return 'required';
      }
    ],
    billing_street: [
      (value?: string) => {
        if (value && !isEmpty(value)) {
          return true;
        }
        return 'required';
      }
    ],
    billing_state: [
      (value?: string) => {
        if (value && !isEmpty(value)) {
          return true;
        }
        return 'required';
      }
    ],
    billing_city: [
      (value?: string) => {
        if (value && !isEmpty(value)) {
          return true;
        }
        return 'required';
      }
    ]
  };

  const [errors, validate, validateObject] = useValidators(
    order,
    fieldValidators,
    (e: ValidationErrors) => {
      if (!isNullOrUndefined(onErrorsChange)) {
        onErrorsChange(filterErrors({ ...ccErrors, ...e }));
      }
    }
  );

  const errorMessages: { [name: string]: { [name: string]: string } } = {
    billing_place: {
      required: 'Billing address is required'
    },
    billing_zip: {
      required: 'Billing zip code is required'
    },
    billing_street: {
      required: 'Billing address is required'
    },
    billing_city: {
      required: 'Billing city is required'
    },
    billing_state: {
      required: 'Billing state is required'
    }
  };

  const handleBillingPlaceChange = (place?: PlaceType) => {
    order.billing_place = pick(place, ['place_id', 'description']) as Place;
    order.billing_address = order.billing_place?.description;

    const placeId = order.billing_place?.place_id;
    if (isEmpty(placeId)) {
      order.billing_zip = '';
      validateObject(order, ['billing_place', 'billing_zip']);
      return;
    }

    getComponentAddressByPlace(place).then(dataPlace => {
      order.billing_zip = dataPlace?.zip_code;
      order.billing_place = dataPlace;
      validateObject(order, ['billing_place', 'billing_zip']);
    });
  };

  const useDeliveryAddress = () => {
    if (order.billing_place && order.customer_place) {
      order.billing_place.place_id = order.customer_place.place_id;
      order.billing_place.description = order.customer_place.description;
      order.billing_address = order.billing_place.description;
    }
    validate('billing_place', order.billing_place);
  };

  // Ensure we always have a billing_place
  order.billing_place = order.billing_place || {};

  const fieldBillingAddress = (
    field: keyof OnlineOrder,
    fieldName: string,
    className?: string
  ) => (
    <TextField
      className={className}
      margin="dense"
      label={fieldName}
      fullWidth
      required
      value={order[field] || ''}
      onChange={event => {
        const value = event.target.value;
        if (field === 'billing_zip') {
          let zipCode = value;
          let v = parseInt(value);
          if (isNaN(v)) {
            zipCode = value.substring(0, value.length - 2);
          }
          if (zipCode.length > 12) {
            zipCode = zipCode.substring(0, 12);
          }

          order.billing_zip = zipCode;
          validate('billing_zip', order.billing_zip);
          return;
        }
        order[field] = value as never;
        validate(field, value);
      }}
      error={!!errors[field]}
      helperText={errorMessages[field][errors[field]]}
    />
  );

  const [checkedApple, setCheckedApple] = useState(
    order.is_payment_by_apple_pay ?? false
  );
  const [checkedCreditCard, setCheckedCreditCard] = useState(
    typeof order.is_payment_by_apple_pay === 'boolean'
      ? !order.is_payment_by_apple_pay
      : false
  );
  const clearErrorSelectPayment = () => {
    const { isSelectPayment, number, name, expiry, cvc, ...rest } = ccErrors;
    handleCCErrorsChange(rest);
  };

  return useObserver(() => {
    return (
      <>
        {isAcceptApplePay ? (
          <>
            <PaymentMethod
              cards={[visa, master, amex, discover]}
              label="Credit card"
              checked={checkedCreditCard}
              onChangeCheckBox={checked => {
                order.is_payment_by_apple_pay = false;
                setCheckedCreditCard(checked);
                checked && setCheckedApple(false);
                clearErrorSelectPayment();
              }}
              rightIcon={creditCard}
            >
              <CreditCardInput
                card={order.card}
                onChange={card => {
                  order.card = { ...card };
                }}
                onErrorsChange={handleCCErrorsChange}
                variant={variant}
              />
            </PaymentMethod>
            <PaymentMethod
              cards={[visa, master]}
              label="Apple Pay"
              checked={checkedApple}
              onChangeCheckBox={checked => {
                order.is_payment_by_apple_pay = true;
                setCheckedApple(checked);
                checked && setCheckedCreditCard(false);
                clearErrorSelectPayment();
              }}
              rightIcon={applePay}
              subLabel="You need compatible Apple device to complete the payment"
            >
              <ApplePayStep />
            </PaymentMethod>
          </>
        ) : (
          <CreditCardInput
            card={order.card}
            onChange={card => {
              order.card = { ...card };
            }}
            onErrorsChange={handleCCErrorsChange}
            variant={variant}
          />
        )}
        <Grid
          container
          direction="row"
          alignItems="center"
          className={classes.headline}
        >
          <Typography variant="h6" color="textSecondary">
            {isManualAddress ? 'Billing info manually' : 'Billing info'}
          </Typography>
          <Switch
            checked={isManualAddress}
            color="primary"
            onChange={v => {
              setIsManualAddress(v.target.checked);
              order.billing_zip = undefined;
              if (v.target.checked) {
                order.billing_place = undefined;
                order.billing_address = undefined;
                validateObject(order, ['billing_zip', 'billing_place']);
              } else {
                order.billing_street = undefined;
                order.billing_city = undefined;
                order.billing_state = undefined;
                validateObject(order, [
                  'billing_zip',
                  'billing_street',
                  'billing_state',
                  'billing_city'
                ]);
              }
            }}
          />
          {order.order_type === 'delivery' && !isManualAddress && (
            <Link
              className={classes.inlineLink}
              component="button"
              onClick={useDeliveryAddress}
            >
              (Use delivery address)
            </Link>
          )}
        </Grid>
        {isManualAddress ? (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {fieldBillingAddress('billing_street', 'Address')}
            </Grid>
            <Grid item xs={6}>
              {fieldBillingAddress('billing_city', 'City')}
            </Grid>
            <Grid item xs={6}>
              {fieldBillingAddress('billing_state', 'State')}
            </Grid>
            <Grid item xs={6}>
              {fieldBillingAddress('billing_zip', 'Zip code')}
            </Grid>
          </Grid>
        ) : (
          <Box display="flex">
            <Box className={classes.address}>
              <GooglePlaceAutocomplete
                label="Billing address *"
                value={order.billing_place as PlaceType}
                onChange={handleBillingPlaceChange}
                error={!!errors.billing_place}
                helperText={errorMessages.billing_place[errors.billing_place]}
              />
            </Box>
            {fieldBillingAddress('billing_zip', 'Zip code', classes.zipcode)}
          </Box>
        )}
      </>
    );
  });
};

export default PaymentInfo;
